import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DialogClose } from '@/components/ui/dialog';

import createApiClient from '@/api/axiosInstance';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Upload,
  Trash2,
  X,
  Image as ImageIcon,
  Image,
  Loader2,
  ShieldAlert,
  CircleCheckBig,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  registerSupplierAction,
  setMachineInfoAction,
} from '@/js/reduxstore/actions/authActions';
import { LockClosedIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import { Label } from '@radix-ui/react-label';
import { useNavigate } from 'react-router-dom';
import { transformApiToRedux } from '@/js/pages/Supplier/ManufacturerDashboard';
import FileUpload from '@/js/components/FileUpload';
import { fetchExistingDocuments } from '@/js/components/FileUploadBox';

const generateMonths = () => [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= 1980; i--) {
    years.push({ value: i.toString(), label: i.toString() });
  }
  return years;
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const DOCUMENT_TITLES = {
  machinePhoto_one: 'Machine Photo 1',
  machinePhoto_two: 'Machine Photo 2',
  machinePhoto_three: 'Machine Photo 3',
  machinePhoto_four: 'Machine Photo 4',
};

// const machiningInformationSchema = z.object({
//   plant_id: z.string().nullable(),
//   machine_name: z.string().min(1, 'Machine name is required'),
//   plant_name: z.string().nullable(),
//   make_and_model: z.string().min(1, 'Make and model is required'),
//   types_of_machine: z.string().min(1, 'Types of machine is required'),
//   machine_capacity: z.string().min(1, 'Machine capacity is required'),
//   last_calibrated_on: z.string().regex(/^\d{4}-\d{2}$/, 'Invalid date format'),
//   up_time: z.number().min(1, 'Up time must be at least 1'),
//   length: z.number().min(1, 'Length must be greater than 0'),
//   breadth: z.number().min(1, 'Breadth must be greater than 0'),
//   height: z.number().min(1, 'Height must be greater than 0'),
//   unit: z.enum(['mm', 'cm', 'm', 'in', 'ft'], 'Invalid unit selected'),
//   number_of_skilled_operators: z
//     .number()
//     .min(0, 'Number of skilled operators must be 0 or greater'),
//   automation: z.string().min(1, 'Automation is required'),
//   age_of_machine: z.number().min(0, '0 represents new machines'),
//   maintenance_cycle: z.number().min(0, 'Maintenance cycle is required'),
//   acceptance_rate: z
//     .number()
//     .min(0, 'Acceptance Rate must be 0 or greater')
//     .max(100, 'Acceptance Rate cannot exceed 100'),
// });
const machiningInformationSchema = z.object({
  plant_id: z.string().nullable(),
  machine_name: z.string().min(1, 'Machine name is required'),
  age_of_machine: z.number().min(0, '0 represents new machines'),
  plant_name: z.string().nullable(),
  make_and_model: z.string().min(1, 'Make and model is required'),
  types_of_machine: z.string().min(1, 'Types of machine is required'),
  machine_capacity: z.string().optional().nullable(),
  length: z.number().nullable().optional(),
  breadth: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  last_calibrated_on: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Invalid date format')
    .optional(),
  up_time: z.number().nullable().optional(),
  length: z.number().nullable().optional(),
  breadth: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  unit: z
    .enum(['mm', 'cm', 'm', 'in', 'ft'], 'Invalid unit selected')
    .optional(),
  number_of_skilled_operators: z.number().nullable().optional(),
  automation: z.string().optional(),
  maintenance_cycle: z.string().nullable().optional(),
  acceptance_rate: z.number().optional().nullable(),
});

const AddMachinePopupForm = ({
  setSectionId,
  identity,
  machineId = null,
  hasMachines,
}) => {
  useEffect(() => {
    console.log('machine identity:', machineId);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const formRef = useRef(null); // for close of the popup
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);
  const reduxData = useSelector(
    (state) => state?.authReducer?.registrationData,
  );
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const token = useSelector((state) => state.authReducer.token);

  const [localPlantInfo, setLocalPlantInfo] = useState(
    reduxData?.plant_info || [],
  );
  const [localMachineInfo, setLocalMachineInfo] = useState(
    reduxData?.machining_info || [],
  );

  useEffect(() => {
    if (reduxData?.plant_info) {
      setLocalPlantInfo(reduxData.plant_info);
    }
    if (reduxData?.machining_info) {
      setLocalMachineInfo(reduxData.machining_info);
    }
    console.log('localPlantInfo:', localPlantInfo);
    console.log('localMachineInfo:', localMachineInfo);
  }, [reduxData, setLocalPlantInfo, setLocalMachineInfo]);

  const thisPlant =
    localPlantInfo.filter((plant) => plant?.plant_id === identity)[0] || null;

  const toEditMachine =
    localMachineInfo?.filter(
      (machine) => machine?.machine_id === machineId,
    )[0] || null;

  console.log('toEditMachine:', toEditMachine);

  // Safer parsing of table_size
  let length = '';
  let breadth = '';
  let height = '';
  let unit = 'mm';

  if (toEditMachine?.table_size) {
    try {
      // Split by space to separate dimensions from unit
      const parts = toEditMachine.table_size.split(' ');

      // Get unit from the second part if it exists
      if (parts.length > 1) {
        unit = parts[1];
      }

      // Split dimensions by 'x'
      const dimensions = parts[0].split('x');

      // Parse each dimension if available
      if (dimensions.length > 0) length = dimensions[0];
      if (dimensions.length > 1) breadth = dimensions[1];
      if (dimensions.length > 2) height = dimensions[2];
    } catch (e) {
      console.error('Error parsing table_size:', e);
      // Use default empty values if parsing fails
    }
  }

  const form = useForm({
    resolver: zodResolver(machiningInformationSchema),
    defaultValues: {
      plant_id: toEditMachine?.plant_id || null,
      machine_name: toEditMachine?.machine_name || '',
      plant_name: toEditMachine?.plant_name || null,
      make_and_model: toEditMachine?.make_and_model || '',
      machine_capacity: toEditMachine?.machine_capacity || null,
      types_of_machine: toEditMachine?.types_of_machine || '',
      maintenance_cycle: toEditMachine?.maintenance_cycle || '',
      last_calibrated_on: toEditMachine?.last_calibrated_on
        ? format(new Date(toEditMachine.last_calibrated_on), 'yyyy-MM')
        : format(new Date(), 'yyyy-MM'),
      length: length ? Number(length) : null,
      breadth: breadth ? Number(breadth) : null,
      height: height ? Number(height) : null,
      unit: unit,
      automation: toEditMachine?.automation || '',
      up_time: toEditMachine?.up_time ? Number(toEditMachine.up_time) : null,
      age_of_machine: toEditMachine?.age_of_machine
        ? Number(toEditMachine.age_of_machine)
        : '',
      number_of_skilled_operators: toEditMachine?.number_of_skilled_operators
        ? Number(toEditMachine.number_of_skilled_operators)
        : null,
      acceptance_rate: toEditMachine?.acceptance_rate
        ? parseFloat(toEditMachine.acceptance_rate)
        : null,
    },
  });

  const [existingDocuments, setExistingDocuments] = useState([]);
  const [documentStates, setDocumentStates] = useState({
    machinePhoto_one: null,
    machinePhoto_two: null,
    machinePhoto_three: null,
    machinePhoto_four: null,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    machinePhoto_one: 0,
    machinePhoto_two: 0,
    machinePhoto_three: 0,
    machinePhoto_four: 0,
  });

  // Fetch existing documents on component mount
  useEffect(() => {
    fetchExistingDocuments(apiClient, setExistingDocuments, setError);
  }, [token]);

  const onSubmit = async (data) => {
    setProcessing(true);
    // Initialize table_size as null
    let tableSize = null;

    // Only create table_size if at least one dimension is provided
    if (data.length || data.breadth || data.height) {
      const length = data.length ?? 0;
      const breadth = data.breadth ?? 0;
      const height = data.height ?? 0;
      const unit = data.unit || 'mm'; // Default to 'mm' if not provided

      tableSize = `${length}x${breadth}x${height} ${unit}`;
    }
    setSaveSuccess(false);
    console.log('Machines Information:', data);
    try {
      const machiningInformation = {
        machine_id: machineId,
        machine_name: data.machine_name,
        plant_name: thisPlant.plant_name,
        plant_id: identity,
        make_and_model: data.make_and_model,
        machine_capacity: data.machine_capacity || null,
        types_of_machine: data.types_of_machine,
        last_calibrated_on: data.last_calibrated_on || null,
        table_size: tableSize,
        automation: data.automation || null,
        up_time: data.up_time ? Number(data.up_time) : null,
        age_of_machine: Number(data.age_of_machine),
        maintenance_cycle: data.maintenance_cycle || null,
        number_of_skilled_operators: data.number_of_skilled_operators
          ? Number(data.number_of_skilled_operators)
          : null,
        acceptance_rate: data.acceptance_rate
          ? Number(data.acceptance_rate)
          : null,
      };

      const isEdit = machineId !== null;

      const endpoint = isEdit
        ? `/api/manufacturer-profile/machining-info/${machineId}`
        : '/api/manufacturer-profile/machining-info';

      const method = isEdit ? 'put' : 'post';

      const response = await apiClient[method](endpoint, machiningInformation);
      const machineData = response.data;

      console.log('Machines Information submitted successfully.', data);
      if (machineData.data) {
        const reduxDocumentSubmission = {
          machinePhoto_one: reduxData?.machinePhoto_one || null,
          machinePhoto_two: reduxData?.machinePhoto_two || null,
          machinePhoto_three: reduxData?.machinePhoto_three || null,
          machinePhoto_four: reduxData?.machinePhoto_four || null,
        };
        const reduxMachineData = {
          ...machiningInformation,
          machine_id: isEdit ? machineId : machineData.data.machine_id,
        };

        dispatch(setMachineInfoAction(reduxMachineData, isEdit));
        setSaveSuccess(true);
        // setLocalMachineInfo((prevMachines) => [
        //   ...prevMachines,
        //   reduxMachineData,
        // ]);
      }
      // Add a delay to show the success message before closing the popup
      setTimeout(() => {
        if (hasMachines === false) {
          setSectionId(7);
        }
        formRef.current?.click();
      }, 1000);

      console.log('Machines Information submitted successfully.');
    } catch (error) {
      console.error('Error submitting Machines Information:', error);
      setError(
        error.response?.data?.error_message ||
          'Something went wrong :( failed to submit Machines Information',
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="w-full bg-white p-8 px-28 rounded-md font-roboto text-gray-600">
      <h2 className="text-3xl font-semibold text-black text-center mb-6">
        Machine Information
      </h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="flex space-x-4">
            <FormField
              control={form.control}
              name="machine_name"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Machine Name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="eg. Vertical Center Machine"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="make_and_model"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Make and Model <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="eg. VF-2SS, Haas Automation, Inc. "
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex space-x-4">
            {/* <FormField
              control={form.control}
              name="plant_name"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Plant which it belongs to (Optional)
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="focus:ring-0 text-gray-600 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple">
                        <SelectValue placeholder="Select the Plant where it belongs" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {localPlantInfo.map((plant) => (
                        <SelectItem
                          key={plant.plant_id}
                          value={plant.plant_name}
                        >
                          {plant.plant_name}
                        </SelectItem>
                      ))}

                      <SelectItem value={null}>None</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            <FormField
              control={form.control}
              name="types_of_machine"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Type of Machine <span className="text-red-500">*</span>
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger
                        className={`focus:ring-0 focus:ring-offset-0  focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  ${field.value ? 'text-black' : 'text-gray-600'} `}
                      >
                        <SelectValue placeholder="Select type of machine" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Manufacturing Machine">
                        Manufacturing Machine
                      </SelectItem>
                      <SelectItem value="QA Lab Equipment">
                        QA Lab Equipment
                      </SelectItem>
                      <SelectItem value="Material Handling Machine">
                        Material Handling Machine
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="age_of_machine"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Machine Age <span className="text-red-500">*</span>
                    <span className="text-gray-500  text-sm"> (in years)</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      placeholder="e.g. 8 year"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex space-x-4">
            <FormField
              control={form.control}
              name="machine_capacity"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Machine Capacity
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. 50kg, 100L, 200pcs"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/*  */}

            <div className="items-end flex flex-1 gap-1">
              <FormField
                control={form.control}
                name="length"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-foreground font-semibold text-gray-700">
                      Table Size
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Length"
                        {...field}
                        type="number"
                        min="0"
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="breadth"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-foreground font-semibold text-gray-700"></FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Breadth"
                        {...field}
                        type="number"
                        min="0"
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="height"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="text-foreground font-semibold text-gray-700"></FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Height"
                        {...field}
                        type="number"
                        min="0"
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel className="ml-1 text-foreground font-semibold text-gray-700">
                      Units
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger
                          className={`focus:ring-0 focus:ring-offset-0  focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  text-black `}
                        >
                          <SelectValue placeholder="Select unit" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="mm">mm</SelectItem>
                        <SelectItem value="cm">cm</SelectItem>
                        <SelectItem value="m">m</SelectItem>
                        <SelectItem value="in">in</SelectItem>
                        <SelectItem value="ft">ft</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <FormField
              control={form.control}
              name="up_time"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Up Time <span className="text-gray-500  ">( per day )</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. 12 hrs"
                      {...field}
                      type="number"
                      min="0"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="number_of_skilled_operators"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Skilled Operators
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      placeholder="e.g. 39"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex space-x-4">
            {/* <FormField
              control={form.control}
              name="automation"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Automation (eg. CNC)
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Information that controls machine"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            <FormField
              control={form.control}
              name="automation"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Automation
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger
                        className={`focus:ring-0 focus:ring-offset-0  focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  ${field.value ? 'text-black' : 'text-gray-600'} `}
                      >
                        <SelectValue placeholder="Select the automation type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Automatic">Automatic</SelectItem>
                      <SelectItem value="Semi-Automatic">
                        Semi-Automatic
                      </SelectItem>
                      <SelectItem value="Manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="last_calibrated_on"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Last Calibrated On
                  </FormLabel>
                  <div className="flex gap-2">
                    {/* Custom Month Select */}
                    <div className="flex-1 relative">
                      <select
                        className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-lightPurple focus:outline-none  text-black focus:ring-1 focus:ring-lightPurple"
                        value={field.value ? field.value.split('-')[1] : ''}
                        onChange={(e) => {
                          const month = e.target.value;
                          const year = field.value
                            ? field.value.split('-')[0]
                            : '';
                          const newValue = year ? `${year}-${month}` : ``;
                          field.onChange(newValue);
                        }}
                      >
                        <option value="" disabled>
                          Select month
                        </option>
                        {generateMonths().map((month) => (
                          <option
                            key={month.value}
                            value={month.value}
                            className="h-8 hover:bg-purple-200"
                          >
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Custom Year Select */}
                    <div className="flex-1 relative">
                      <select
                        className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-lightPurple focus:outline-none focus:ring-1 focus:ring-lightPurple text-black"
                        value={field.value ? field.value.split('-')[0] : ''}
                        onChange={(e) => {
                          const year = e.target.value;
                          const month = field.value
                            ? field.value.split('-')[1]
                            : '';
                          const newValue = month ? `${year}-${month}` : ``;
                          field.onChange(newValue);
                        }}
                      >
                        <option value="" disabled>
                          Select year
                        </option>
                        {generateYears().map((year) => (
                          <option
                            key={year.value}
                            value={year.value}
                            className="h-8 hover:bg-purple-200"
                          >
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex space-x-4">
            {/* <FormField
              control={form.control}
              name="maintenance_cycle"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Maintenance Cycle
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      placeholder="e.g. 2 times a month"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            <FormField
              control={form.control}
              name="maintenance_cycle"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Maintenance Cycle
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger
                        className={`focus:ring-0 focus:ring-offset-0  focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  ${field.value ? 'text-black' : 'text-gray-600'} `}
                      >
                        <SelectValue placeholder="Select maintenance cycle" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Once a year">Yearly</SelectItem>
                      <SelectItem value="Once every 3 months">
                        Quarterly (3 months)
                      </SelectItem>
                      <SelectItem value="Once a month">Monthly</SelectItem>
                      <SelectItem value="Once a week">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="acceptance_rate"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Acceptance Rate <span className="text-gray-500  ">(%)</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="e.g. 95%"
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col gap-2 justify-between items-between">
            <div>
              <span className="text-foreground text-sm font-semibold text-gray-700">
                Recent photos of Machine
              </span>
            </div>
            <div className="flex justify-between mb-4">
              {[
                {
                  name: 'Machine Photo 1',
                  label: 'Upload Photo',
                  existingKey: existingDocuments.find(
                    (doc) => doc.document_name === 'Machine Photo 1',
                  ),
                },
                {
                  name: 'Machine Photo 2',
                  label: 'Upload Photo',
                  existingKey: existingDocuments.find(
                    (doc) => doc.document_name === 'Machine Photo 2',
                  ),
                },
                {
                  name: 'Machine Photo 3',
                  label: 'Upload Photo',
                  existingKey: existingDocuments.find(
                    (doc) => doc.document_name === 'Machine Photo 3',
                  ),
                },
                {
                  name: 'Machine Photo 4',
                  label: 'Upload Photo',
                  existingKey: existingDocuments.find(
                    (doc) => doc.document_name === 'Machine Photo 4',
                  ),
                },
              ].map(({ name, label, existingKey }) => (
                <FileUpload
                  DOCUMENT_TITLES={DOCUMENT_TITLES}
                  MAX_FILE_SIZE={MAX_FILE_SIZE}
                  ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                  existingDocuments={existingDocuments}
                  setExistingDocuments={setExistingDocuments}
                  documentStates={documentStates}
                  setDocumentStates={setDocumentStates}
                  uploadProgress={uploadProgress}
                  setUploadProgress={setUploadProgress}
                  isUploading={isUploading}
                  setIsUploading={setIsUploading}
                  setError={setError}
                  key={name}
                  id={name}
                  label={label}
                  documentType={name}
                  existingDocument={existingKey}
                  classNames="w-36 h-24"
                />
              ))}
            </div>
          </div>
          <div className="flex justify-center mt-6 text-lg">
            {/* <DialogClose>
              <Button
                type="button"
                variant="outline"
                className="px-4 py-2 h-12 w-36 hover:bg-slate-200"
              >
                Back
              </Button>
            </DialogClose> */}
            <Button
              type="submit"
              className={`px-5 py-5 w-28 ease-out duration-300 ${
                saveSuccess
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-darkPurple hover:bg-darkerPurple'
              }`}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="font-roboto font-semibold text-base">
                    Saving...
                  </span>
                </>
              ) : saveSuccess ? (
                <>
                  <CircleCheckBig className=" h-4 w-4" />
                  <span className="font-roboto font-semibold text-base">
                    Saved
                  </span>
                </>
              ) : (
                <span className="font-roboto font-semibold text-base">
                  Save
                </span>
              )}
            </Button>
            <DialogClose ref={formRef} className="hidden" />
          </div>

          <div className="flex flex-row items-center justify-center space-x-2 ">
            {/* conditional renderign if no error display this if error dispaly error instead */}
            {error ? (
              <>
                <ShieldAlert className="h-3 w-3" />
                <span className="text-xs text-red-600">{error}</span>
              </>
            ) : (
              <>
                <LockClosedIcon className="h-3 w-3" />
                <span className="text-xs text-[#2CD673]">
                  We use industry-standard encryption to protect your data
                </span>
              </>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AddMachinePopupForm;
